import {
  Box,
  Flex,
  Heading,
  Icon,
  SimpleGrid,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
} from '@chakra-ui/react'
import {
  LockIcon,
  ViewIcon,
  StarIcon,
  ExternalLinkIcon,
  InfoOutlineIcon,
} from '@chakra-ui/icons'
import numeral from 'numeral'
import { motion } from 'framer-motion'
import CmsTrackingCsvDownloadButton from 'src/components/FullDashboardCell/CmsTrackingCsvDownloadButton'
import {
  ANALYTICS_EXPORT_COLUMNS,
  ANALYTICS_SITES_EXPORT_QUERY,
  COOKIE_CONSENT_EXPORT_COLUMNS,
  COOKIE_CONSENT_SITES_EXPORT_QUERY,
} from 'src/components/FullDashboardCell/cookieAnalyticsExportShared'

const MotionBox = motion(Box)

const COOKIE_METRIC_KEYS = ['cookie_banner']

const ANALYTICS_METRIC_KEYS = [
  'umami',
  'beehively_gtag',
  'school_ga4',
  'gtm',
  'meta_pixel',
]

/** API uses stable keys; map older GraphQL label strings from cache. */
const LEGACY_TYPE_TO_KEY = {
  'Cookie banner': 'cookie_banner',
  cookie_banner: 'cookie_banner',
  Umami: 'umami',
  umami: 'umami',
  'Beehively Gtag': 'beehively_gtag',
  Beehively: 'beehively_gtag',
  beehively_gtag: 'beehively_gtag',
  'Google Analytics (GA4)': 'school_ga4',
  GA4: 'school_ga4',
  school_ga4: 'school_ga4',
  'Google Tag Manager (GTM)': 'gtm',
  GTM: 'gtm',
  gtm: 'gtm',
  'Meta Pixel code': 'meta_pixel',
  'Meta Pixel': 'meta_pixel',
  meta_pixel: 'meta_pixel',
}

const METRIC_CONFIG = {
  cookie_banner: {
    label: 'Cookie banner',
    icon: LockIcon,
    accent: 'teal.600',
    accentBg: 'teal.50',
    barColor: 'teal.500',
    help: 'cookie_consent_enabled on a CMS site',
  },
  umami: {
    label: 'Umami',
    icon: ViewIcon,
    accent: 'cyan.700',
    accentBg: 'cyan.50',
    barColor: 'cyan.500',
    help: 'umami_id on a CMS site',
  },
  beehively_gtag: {
    label: 'Beehively Gtag',
    icon: StarIcon,
    accent: 'purple.600',
    accentBg: 'purple.50',
    barColor: 'purple.500',
    help: 'beehively_gtag_code (Beehively GA4 in CMS admin)',
  },
  school_ga4: {
    label: 'Google Analytics (GA4)',
    icon: InfoOutlineIcon,
    accent: 'orange.600',
    accentBg: 'orange.50',
    barColor: 'orange.400',
    help: 'gtag_code (school GA4 IDs in CMS admin)',
  },
  gtm: {
    label: 'Google Tag Manager (GTM)',
    icon: ExternalLinkIcon,
    accent: 'green.700',
    accentBg: 'green.50',
    barColor: 'green.500',
    help: 'gtm_code in CMS admin',
  },
  meta_pixel: {
    label: 'Meta Pixel code',
    icon: ViewIcon,
    accent: 'blue.700',
    accentBg: 'blue.50',
    barColor: 'blue.600',
    help: 'meta_pixel_code in CMS admin',
  },
}

const adoptionPercent = (count, total) => {
  if (!total || total <= 0) return 0
  return Math.min(100, Math.round((count / total) * 100))
}

const normalizeCounts = (cookieAnalytics) => {
  const byKey = {}
  for (const row of cookieAnalytics || []) {
    const key = LEGACY_TYPE_TO_KEY[row.type] || row.type
    const count = Number(row.count)
    if (!Number.isNaN(count)) {
      byKey[key] = count
    }
  }
  return byKey
}

const buildRows = (keys, countsByKey) =>
  keys.map((key) => ({
    key,
    count: countsByKey[key] ?? 0,
  }))

const MetricCard = ({ metricKey, count, totalSites, index, compact }) => {
  const config = METRIC_CONFIG[metricKey] || {
    label: metricKey,
    icon: ViewIcon,
    accent: 'gray.600',
    accentBg: 'gray.100',
    barColor: 'gray.400',
    help: '',
  }
  const pct = adoptionPercent(count, totalSites)

  return (
    <MotionBox
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      p={compact ? 3 : 4}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      bg="white"
      boxShadow="sm"
      height="100%"
    >
      <Flex align="flex-start" gap={3} mb={3}>
        <Flex
          align="center"
          justify="center"
          boxSize={compact ? 8 : 10}
          borderRadius="full"
          bg={config.accentBg}
          color={config.accent}
          flexShrink={0}
        >
          <Icon as={config.icon} boxSize={compact ? 4 : 5} />
        </Flex>
        <Stat flex="1" margin={0}>
          <StatLabel
            fontSize="sm"
            fontWeight={500}
            color="gray.600"
            textTransform="none"
            letterSpacing="normal"
          >
            {config.label}
          </StatLabel>
          <StatNumber
            fontSize={compact ? '2xl' : '3xl'}
            fontWeight={400}
            lineHeight="1.1"
          >
            {numeral(count).format()}
          </StatNumber>
          <StatHelpText marginTop={1} mb={0} fontSize="xs">
            of {numeral(totalSites).format()} active CMS sites
          </StatHelpText>
        </Stat>
        <Text
          fontSize="sm"
          fontWeight={600}
          color={config.accent}
          alignSelf="flex-start"
          mt={1}
        >
          {pct}%
        </Text>
      </Flex>
      <Box
        h="5px"
        bg="gray.100"
        borderRadius="full"
        overflow="hidden"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${config.label}: ${pct}% of CMS sites`}
      >
        <Box
          h="100%"
          w={`${pct}%`}
          bg={config.barColor}
          borderRadius="full"
          transition="width 0.45s ease-out"
        />
      </Box>
      {config.help ? (
        <Text fontSize="xs" color="gray.500" mt={2} lineHeight="short">
          {config.help}
        </Text>
      ) : null}
    </MotionBox>
  )
}

const CookieAnalyticsSection = ({
  cookieAnalytics = [],
  totalSites = 0,
}) => {
  const countsByKey = normalizeCounts(cookieAnalytics)
  const cookieRows = buildRows(COOKIE_METRIC_KEYS, countsByKey)
  const analyticsRows = buildRows(ANALYTICS_METRIC_KEYS, countsByKey)

  return (
    <Stack spacing={6} mb={8} pb={6}>
      <Box
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        bg="gray.50"
      >
        <Flex
          align={{ base: 'stretch', sm: 'center' }}
          justify="space-between"
          gap={3}
          mb={1}
          direction={{ base: 'column', sm: 'row' }}
        >
          <Heading size="sm" fontWeight={500}>
            Cookie consent
          </Heading>
          <CmsTrackingCsvDownloadButton
            query={COOKIE_CONSENT_SITES_EXPORT_QUERY}
            resultKey="cookieConsentSitesExport"
            columns={COOKIE_CONSENT_EXPORT_COLUMNS}
            filenamePrefix="cookie-consent-sites"
            successLabel="sites with consent banner enabled"
          />
        </Flex>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Active CMS sites (non-dormant school, not a test school) with the
          consent banner enabled.
        </Text>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
          {cookieRows.map((row, index) => (
            <MetricCard
              key={row.key}
              metricKey={row.key}
              count={row.count}
              totalSites={totalSites}
              index={index}
            />
          ))}
        </SimpleGrid>
      </Box>

      <Box
        p={4}
        border="1px solid"
        borderColor="gray.200"
        borderRadius="lg"
        bg="gray.50"
      >
        <Flex
          align={{ base: 'stretch', sm: 'center' }}
          justify="space-between"
          gap={3}
          mb={1}
          direction={{ base: 'column', sm: 'row' }}
        >
          <Heading size="sm" fontWeight={500}>
            Analytics
          </Heading>
          <CmsTrackingCsvDownloadButton
            query={ANALYTICS_SITES_EXPORT_QUERY}
            resultKey="analyticsSitesExport"
            columns={ANALYTICS_EXPORT_COLUMNS}
            filenamePrefix="analytics-sites"
            successLabel="active CMS sites"
          />
        </Flex>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Active CMS sites (non-dormant school, not a test school). Same fields
          as CMS admin Cookies &amp; Analytics — one count per CMS site.
        </Text>
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={3}>
          {analyticsRows.map((row, index) => (
            <MetricCard
              key={row.key}
              metricKey={row.key}
              count={row.count}
              totalSites={totalSites}
              index={index}
              compact
            />
          ))}
        </SimpleGrid>
      </Box>
    </Stack>
  )
}


export default CookieAnalyticsSection
